<template>
  <div>
    <v-dialog width="80%" v-model="dialog" scrollable v-if="dialog">
      <v-card  class="billing-request-show">
        <v-divider />


        <v-card-text class="pa-10">
          <v-row>
            <v-img src="~@images/logo.svg" max-width="250" contain />
            <v-spacer></v-spacer>
            <v-col cols="auto" class="billing-request-number">
              <v-chip>
                {{ $t('billing-request.statues.' + billingRequest.status) }}
                <v-tooltip bottom v-if="billingRequest.treatedExternally">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" color="warning" small class="ml-2">
                      mdi-briefcase-check
                    </v-icon>
                  </template>
                  <span>{{ $t('billing-request.treated-externally-tooltip') }}</span>
                </v-tooltip>
              </v-chip>
              <span  class="ml-6">
                {{ $t('billing-request.billing-request-number', {number: billingRequest.billingRequestReference}) }}
              </span>
            </v-col>
          </v-row>
          <v-row class="mt-10">
            <v-col cols="auto" class="billing-request-address">
              <b class="name">{{ billingRequest.issuerName }}</b>
              <div v-if="billingRequest.issuerAddress" v-html="formatAddress(billingRequest.issuerAddress)"></div>
              <div>{{ $t('account-number') }} : <b>{{ billingRequest.issuerAccountNumber }}</b></div>
              <div v-if="billingRequest.issuerRegistrationNumber">{{ $t('organization.registration-number') }} : <b>{{ billingRequest.issuerRegistrationNumber }}</b></div>
              <div v-if="billingRequest.issuerVatNumber">{{ $t('vat-number') }} : <b>{{ billingRequest.issuerVatNumber }}</b></div>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="billing-request-address">
              <b class="name">{{ billingRequest.recipientName }}</b>
              <div v-if="billingRequest.recipientAddress" v-html="formatAddress(billingRequest.recipientAddress)"></div>
              <div>{{ $t('account-number') }} : <b>{{ billingRequest.recipientAccountNumber }}</b></div>
              <div v-if="billingRequest.recipientRegistrationNumber">{{ $t('organization.registration-number') }} : <b>{{ billingRequest.recipientRegistrationNumber }}</b></div>
              <div v-if="billingRequest.recipientVatNumber">{{ $t('vat-number') }} : <b>{{ billingRequest.recipientVatNumber }}</b></div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="auto" class="billing-request-infos">
              <div v-html="$t('billing-request.period-from-to', {start: formatUtcDate(billingRequest.startDate, $t('format_date')), end: formatUtcDate(billingRequest.endDate, $t('format_date')) }) "></div>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="auto" class="text-right align-end d-flex">
              <div>{{ $t('created-at')}} <b>{{ billingRequest.createdAt | formatDate($t('format_date')) }}</b></div>
            </v-col>
          </v-row>

         <v-row v-for="section in sections" :key="section.label">
            <v-col>
              <h4 class="mb-1">{{ section.label }}</h4>
              <v-simple-table class="billing-request-lines">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th width="100" class="text-center">{{ $t('invoice.product-code') }}</th>
                      <th>{{ $t('label') }}</th>
                      <th width="40" class="text-center">{{ $t('invoice.quantity') }}</th>
                      <th width="100" class="text-right">{{ $t('invoice.unit-price') }}</th>
                      <th width="100" class="text-right">{{ $t('invoice.vat-rate') }}</th>
                      <th width="100" class="text-center">{{ $t('country') }}</th>
                      <th width="150" class="text-right">{{ $t('invoice.total-ht') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(type, t) in section.types">
                      <tr :key="section.label + '::' + t">
                        <td colspan="7" class="billing-request-line-type">{{ $t('invoice.line-types.' + type.label) }}</td>
                      </tr>
                      <tr v-for="line in type.lines" :key="line.id">
                        <td class="text-center">{{ line.productCode }}</td>
                        <td>
                          <div><b>{{ line.label }}</b></div>
                          <div v-for="(descLine, i) in line.description?.split('\n')" :key="line.id + '::' + i">{{ descLine }}</div>
                        </td>
                        <td class="text-center">{{ line.quantity }}</td>
                        <td class="text-right">{{ numberFormat(line.unitPrice) }} €</td>
                        <td class="text-right">{{ line.selfLiquidation ? $t('invoice.self-liquidation') : line.vatRate + ' %' }} </td>
                        <td class="text-center">{{ line.country }}</td>
                        <td class="text-right ">{{ numberFormat(line.netTotal) }} €</td>
                      </tr>
                    </template>

                    <tr class="total">
                      <td colspan="5" class="no-border"></td>
                      <td class="text-center">{{ $t('invoice.total-ht') }}</td>
                      <td class="text-right font-weight-bold">{{ numberFormat(section.total) }} €</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-simple-table class="billing-request-bases">
                <template v-slot:default>
                  <thead>
                  <tr>
                    <th width="100" class="text-right">{{ $t('invoice.base-ht') }}</th>
                    <th width="100" class="text-right">{{ $t('invoice.vat-rate') }}</th>
                    <th width="100" class="text-center">{{ $t('country') }}</th>
                    <th width="150" class="text-right">{{ $t('invoice.vat-amount') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(base, k) in bases" :key="k">
                      <td class="text-right">{{ numberFormat(base.amount) }} €</td>
                      <td class="text-right">{{ base.label }}</td>
                      <td class="text-center">{{ base.country }}</td>
                      <td class="text-right">{{ base.selfLiquidation ? '-' : numberFormat(base.vatAmount) + ' €' }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
            <v-spacer></v-spacer>
            <v-col>
              <v-simple-table>
                <template v-slot:default>
                  <tbody>
                    <tr>
                      <td width="150" class="text-right">Montant HT</td>
                      <td width="150" class="text-right">{{ numberFormat(billingRequest.netTotal) }} €</td>
                    </tr>
                    <tr>
                      <td class="text-right">TVA</td>
                      <td class="text-right">{{ numberFormat(billingRequest.vatTotal) }} €</td>
                    </tr>
                    <tr>
                      <td class="text-right font-weight-bold">Montant TTC</td>
                      <td class="text-right font-weight-bold">{{ numberFormat(billingRequest.finalTotal) }} €</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>



          <template v-if="billingRequest.billingRequestInvoices?.length > 0">
            <v-divider class="mt-10 mb-3" />
            <headline>
              {{ $tc('billing-request-invoice.billing-request-invoice', billingRequest.billingRequestInvoices.length) }}
            </headline>
            <v-data-table
              :items="billingRequest.billingRequestInvoices"
              :headers="billingRequestInvoiceHeaders"
              :hide-default-footer="billingRequest.billingRequestInvoices?.length < 11 ?? false"
            >
              <template #item.createdBy="{item}">
                {{ item.createdBy ? item.createdBy.firstname + ' ' + item.createdBy.lastname : '-' }}
              </template>

              <template #item.createdAt="{item}">
                {{ item.createdAt ? formatDate(item.createdAt, $t('format_date')) : '-' }}
              </template>

             <template #item.reviewedBy="{item}">
                {{ item.reviewedBy ? item.reviewedBy.firstname + ' ' + item.reviewedBy.lastname : '-' }}
             </template>

             <template #item.reviewedAt="{item}">
                {{ item.reviewedAt ? formatDate(item.reviewedAt, $t('format_date')) : '-' }}
             </template>

              <template #item.status="{item}">
                {{ $t('billing-request-invoice.statues.' + item.status) }}
              </template>

              <template #item.actions="{item}">
                <v-btn class="my-3" small icon :to="{name: 'billing-requests.invoices.show', params: {id: item.id}}" exact target="_blank">
                  <v-icon>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </template>

        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="auto">
              <v-btn small @click="download" type="button">
                {{ $t('invoice.download-pdf') }}
              </v-btn>
              <v-btn class="ml-3" small @click="downloadAttachment" type="button">
                {{ $t('invoice.download-attachments') }}
              </v-btn>
              <v-btn class="ml-3" small @click="updateBillingDetails" type="button" v-if="canUpdateBillingDetails">
                {{ $t('invoice.update-billing-details') }}
              </v-btn>
              <v-btn class="ml-3" small @click="createBillingRequestInvoice" type="button" v-if="canSendInvoice">
                {{ $t('billing-request.prepare-invoice') }}
              </v-btn>

              <span class="ml-13"></span>

              <v-btn class="ml-3" outlined color="error" small @click="cancel" type="button" v-if="currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE')  && canCancel">
                {{ $t('billing-request.cancel-billing-request') }}
              </v-btn>

              <v-btn class="ml-3" outlined color="warning" small @click="restore" type="button" v-if="currentUser.isZE && currentUser.permissions.includes('OPERATOR_WRITE') && isCanceled">
                {{ $t('billing-request.restore-billing-request') }}
              </v-btn>

              <v-btn class="ml-3" outlined color="error" small @click="markAsTreatedExternally" type="button" v-if="currentUser.isZE && currentUser.permissions.includes('INVOICE_WRITE') && canMarkAsTreatedExternally">
                {{ $t('billing-request.mark-as-treated-externally') }}
              </v-btn>

              <v-btn class="ml-3" outlined color="warning" small @click="cancelExternalTreatment" type="button" v-if="currentUser.isZE && currentUser.permissions.includes('INVOICE_WRITE') && billingRequest.treatedExternally">
                {{ $t('billing-request.cancel-external-treatment') }}
              </v-btn>
            </v-col>
            <v-col class="text-right">
              <v-btn small class="primary" @click="dialog = false" type="button">
                {{ $t('close') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BillingRequestRepository from "@repository/BillingRequestRepository";
import InvoiceRepository from "@repository/InvoiceRepository";

export default {

  components: {

  },

  data() {
    return {
      billingRequestId: null,
      dialog: false,
      billingRequest: null,
    }
  },

  watch: {

  },

  computed: {
    billingRequestInvoiceHeaders() {
      return [
        { text: this.$t('created-by'), value: 'createdBy' },
        { text: this.$t('created-at'), value: 'createdAt' },
        { text: this.$t('billing-request-invoice.invoice-reference'), value: 'reference' },
        { text: this.$t('reviewed-by'), value: 'reviewedBy' },
        { text: this.$t('reviewed-at'), value: 'reviewedAt' },
        { text: this.$t('status'), value: 'status' },
        { value: 'actions' },
      ]
    },

    isInGeneration() {
      return this.billingRequest.status === 'in-generation'
    },

    isTreated() {
      return this.billingRequest.status === 'treated'
    },

    isCanceled() {
      return this.billingRequest.status === 'canceled'
    },

    canSendInvoice() {
      if(this.isCanceled || this.isTreated || this.isInGeneration) {
        return false
      }
      let count = this.billingRequest?.billingRequestInvoices?.filter(i => [
        'draft',
        'waiting-for-review',
        'validated',
      ].includes(i.status)).length ?? 0
      return count === 0
    },

    canUpdateBillingDetails() {
      return !this.isCanceled && !this.isTreated
    },

    canCancel() {
      return !this.isCanceled && ['ready-to-invoice'].includes(this.billingRequest.status)
    },

    canMarkAsTreatedExternally() {
      return ['ready-to-invoice'].includes(this.billingRequest.status) && !this.billingRequest.treatedExternally
    },

    sections() {
      const sections = {}
      for(let item of this.billingRequest.lines) {
        if(!sections[item.section]) {
          sections[item.section] = {
            label: item.section,
            types: {},
            total: 0,
          }
        }
        if(!sections[item.section].types[item.type]) {
          sections[item.section].types[item.type] = {
            label: item.type,
            lines: [],
            total: 0
          }
        }
        sections[item.section].total += item.netTotal
        sections[item.section].types[item.type].lines.push(item)
        sections[item.section].types[item.type].total += item.netTotal
      }


      const finalSections = []
      for(let section in sections) {

        const newSection = {
          label: sections[section].label,
          types: [],
          total: sections[section].total
        }

        for(const type in sections[section].types) {
          newSection.types.push(sections[section].types[type])
        }

        finalSections.push(newSection)
      }

      return finalSections
    },

    bases() {
      const bases = {}

      for(let item of this.billingRequest.lines) {
        if(item.isDisbursement) {
          continue
        }

        let b = item.vatRate
        if(item.selfLiquidation) {
          b = 'auto'
        }
        b += '_' + item.country

        if(!bases[b]) {
          bases[b] = {
            label: item.selfLiquidation ? this.$t('invoice.self-liquidation') : item.vatRate + '%',
            country: item.country,
            amount: 0,
            vatAmount: 0,
            selfLiquidation: item.selfLiquidation,
          }
        }
        bases[b].amount += item.netTotal
      }

      for(let b in bases) {
        let item = bases[b]
        bases[b].vatAmount = item.amount * (item.selfLiquidation ? 0 : item.label.replace('%', '') / 100)
      }

      if(Object.keys(bases).length === 0) {
        return {
          "empty": {

          }
        }
      }

      return bases
    }
  },


  methods: {
    open(billingRequestId) {
      this.billingRequestId = billingRequestId
      this.loadData().then(() => {
        this.dialog = true
      })
    },

    close() {
      this.dialog = false
    },

    loadData() {
      return new Promise((resolve, reject) => {
        this.showLoading(true)
        BillingRequestRepository.get(this.billingRequestId).then(billingRequest => {
          this.billingRequest = billingRequest
          this.showLoading(false)
          resolve(billingRequest)
        }).catch(error => {
          this.notifyError(error)
          this.showLoading(false)
        })
      })
    },

    formatAddress(address) {
      if(!address) {
        return ''
      }

      const lines = []

      let str = ''
      if(address.number) {
        str += address.number + ', '
      }
      if(address.street) {
        str += address.street
      }
      if(str) {
        lines.push(str)
      }

      str = ''
      if(address.zipcode) {
        str += address.zipcode + ' '
      }
      if(address.city) {
        str += address.city
      }
      if(str) {
        lines.push(str)
      }

      if(address.country) {
        lines.push(address.country)
      }
      return lines.join('<br>')
    },

    download() {
      this.showLoading(true)
      BillingRequestRepository.inlinePdf(this.billingRequest.id).then(url => {
        window.open(url, "_blank");
        this.showLoading(false)
      }).catch(error => {
        this.notifyError(error)
        this.showLoading(false)
      })
    },

    downloadAttachment() {
      this.showLoading(true)
      BillingRequestRepository.downloadAttachment(this.billingRequest.id).then(url => {
        this.showLoading(false)
      }).catch(error => {
        this.notifyError(error)
        this.showLoading(false)
      })
    },

    updateBillingDetails() {
      this.showLoading(true)
      BillingRequestRepository.updateBillingDetails(this.billingRequest.id).then(url => {
        this.loadData()
      }).catch(error => {
        this.notifyError(error)
        this.showLoading(false)
      })
    },

    createBillingRequestInvoice() {
      this.showLoading(true)
      BillingRequestRepository.createBillingDetailInvoice(
          this.billingRequest.organizationId,
          null,
          [this.billingRequest.id],
          null,
      ).then(data => {
        this.showLoading(false)
        this.close()
        this.$emit('updated')
        this.$router.push({name: 'billing-requests.invoices.edit', params: {id: data.id}})
      }).catch(err => {
        this.notifyError(err)
        this.showLoading(false)
      })

    },

    cancel() {
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(result => {
        if (result) {
          this.showLoading(true)
          BillingRequestRepository.cancel(this.billingRequest.id).then(data => {
            this.showLoading(false)
            this.loadData()
            this.$emit('updated')
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      })
    },

    restore() {
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(result => {
        if (result) {
          this.showLoading(true)
          BillingRequestRepository.restore(this.billingRequest.id).then(data => {
            this.showLoading(false)
            this.loadData()
            this.$emit('updated')
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      })
    },

    markAsTreatedExternally() {
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(result => {
        if (result) {
          this.showLoading(true)
          BillingRequestRepository.markAsTreatedExternally(this.billingRequest.id).then(data => {
            this.showLoading(false)
            this.loadData()
            this.$emit('updated')
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      })
    },

    cancelExternalTreatment() {
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(result => {
        if (result) {
          this.showLoading(true)
          BillingRequestRepository.cancelExternalTreatment(this.billingRequest.id).then(data => {
            this.showLoading(false)
            this.loadData()
            this.$emit('updated')
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      })
    },

  }
}
</script>

<style lang="scss">
.billing-request-show {
  .billing-request-number {
    font-size:20px;
    font-weight: bold;
  }

  .billing-request-address {
    min-width: 400px;
  }

  .v-data-table {
    tr {
      &:hover {
        background-color: transparent !important;
      }
      th {
        border-top: 1px solid #e0e0e0 !important;
        border-left: 1px solid #e0e0e0 !important;
        &:last-child {
          border-right: 1px solid #e0e0e0 !important;
        }
      }
      td {
        border-top: 1px solid #e0e0e0 !important;
        border-left: 1px solid #e0e0e0 !important;
        height: 30px !important;
        &.billing-request-line-type {
          text-align: center;
          background: #eee;
        }
        &.no-border {
          border-left: 0 !important;
        }
        &:last-child {
          border-right: 1px solid #e0e0e0 !important;
        }
      }
      &:last-child {
        td:not(.no-border) {
          border-bottom: 1px solid #e0e0e0 !important;
        }
      }
    }
  }
}
</style>
